import { m, useReducedMotion, type Variants } from "framer-motion";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { StackedTokenIcons } from "~/components/stacked-token-icons";
import { fluidSprings } from "~/components/ui/fluid-motion";
import { cn } from "~/lib/utils";
import "./portfolio-hero-card.css";

const FIGMA_CARD_WIDTH_PX = 318;
const FIGMA_CARD_INLINE_INSET_PX = 20;
const FIGMA_TOKEN_SIZE_PX = 28;
const FIGMA_TOKEN_OVERLAP_PX = 14;
const AVAILABLE_TOKEN_ROW_WIDTH_PX =
  FIGMA_CARD_WIDTH_PX - FIGMA_CARD_INLINE_INSET_PX * 2;
const MAX_VISIBLE_TOKEN_SLOTS =
  Math.floor(
    (AVAILABLE_TOKEN_ROW_WIDTH_PX - FIGMA_TOKEN_SIZE_PX) /
      (FIGMA_TOKEN_SIZE_PX - FIGMA_TOKEN_OVERLAP_PX),
  ) + 1;

function getVisibleTokenSlotCount(cardWidthPx: number): number {
  if (!Number.isFinite(cardWidthPx) || cardWidthPx <= 0) {
    return MAX_VISIBLE_TOKEN_SLOTS;
  }

  const availableWidthPx =
    cardWidthPx * (AVAILABLE_TOKEN_ROW_WIDTH_PX / FIGMA_CARD_WIDTH_PX);
  const slotCount =
    Math.floor(
      (availableWidthPx - FIGMA_TOKEN_SIZE_PX) /
        (FIGMA_TOKEN_SIZE_PX - FIGMA_TOKEN_OVERLAP_PX),
    ) + 1;

  return Math.max(1, Math.min(slotCount, MAX_VISIBLE_TOKEN_SLOTS));
}

const CARD_MOTION_VARIANTS = {
  idle: {
    boxShadow: "0 0 0 rgba(0, 0, 0, 0)",
    scale: 1,
    transition: fluidSprings.moderate.exit,
    y: 0,
  },
  hover: {
    boxShadow: "0 18px 38px rgba(0, 0, 0, 0.34)",
    scale: 1.012,
    transition: fluidSprings.moderate,
    y: -4,
  },
  pressed: {
    boxShadow: "0 8px 18px rgba(0, 0, 0, 0.26)",
    scale: 0.992,
    transition: fluidSprings.fast,
    y: -1,
  },
  selected: {
    boxShadow: "0 14px 34px rgba(47, 182, 71, 0.16)",
    scale: 1.006,
    transition: fluidSprings.moderate,
    y: -2,
  },
  selectedHover: {
    boxShadow: "0 20px 44px rgba(47, 182, 71, 0.22)",
    scale: 1.014,
    transition: fluidSprings.moderate,
    y: -5,
  },
  selectedPressed: {
    boxShadow: "0 9px 22px rgba(47, 182, 71, 0.18)",
    scale: 0.994,
    transition: fluidSprings.fast,
    y: -1,
  },
} satisfies Variants;

const IMAGE_MOTION_VARIANTS = {
  idle: { scale: 1, transition: fluidSprings.moderate.exit },
  hover: { scale: 1.035, transition: fluidSprings.moderate },
  pressed: { scale: 1.018, transition: fluidSprings.fast },
  selected: { scale: 1.018, transition: fluidSprings.moderate },
  selectedHover: { scale: 1.045, transition: fluidSprings.moderate },
  selectedPressed: { scale: 1.025, transition: fluidSprings.fast },
} satisfies Variants;

const GLINT_MOTION_VARIANTS = {
  idle: { opacity: 0, transition: fluidSprings.moderate.exit },
  hover: { opacity: 1, transition: fluidSprings.moderate },
  pressed: { opacity: 0.55, transition: fluidSprings.fast },
  selected: { opacity: 0, transition: fluidSprings.moderate.exit },
  selectedHover: { opacity: 0.75, transition: fluidSprings.moderate },
  selectedPressed: { opacity: 0.45, transition: fluidSprings.fast },
} satisfies Variants;

const REDUCED_CARD_MOTION_VARIANTS = Object.fromEntries(
  Object.entries(CARD_MOTION_VARIANTS).map(([key, value]) => [
    key,
    { ...value, scale: 1, transition: { duration: 0 }, y: 0 },
  ]),
) satisfies Variants;

const REDUCED_IMAGE_MOTION_VARIANTS = Object.fromEntries(
  Object.keys(IMAGE_MOTION_VARIANTS).map((key) => [
    key,
    { scale: 1, transition: { duration: 0 } },
  ]),
) satisfies Variants;

const REDUCED_GLINT_MOTION_VARIANTS = Object.fromEntries(
  Object.entries(GLINT_MOTION_VARIANTS).map(([key, value]) => [
    key,
    { ...value, transition: { duration: 0 } },
  ]),
) satisfies Variants;

export interface PortfolioHeroCardToken {
  readonly symbol: string;
  readonly url: string;
}

const EMPTY_PORTFOLIO_HERO_CARD_TOKENS: readonly PortfolioHeroCardToken[] = [];

interface PortfolioHeroCardProps {
  readonly accessibleLabel: string;
  readonly className?: string;
  readonly imageSrc: string;
  readonly onSelect: () => void;
  readonly performanceLabel: ReactNode;
  readonly performanceTone?: "negative" | "neutral" | "positive";
  readonly performanceValue: ReactNode;
  readonly selected: boolean;
  readonly title: ReactNode;
  readonly tokens?: readonly PortfolioHeroCardToken[];
}

export function PortfolioHeroCard({
  accessibleLabel,
  className,
  imageSrc,
  onSelect,
  performanceLabel,
  performanceTone = "neutral",
  performanceValue,
  selected,
  title,
  tokens = EMPTY_PORTFOLIO_HERO_CARD_TOKENS,
}: PortfolioHeroCardProps) {
  const reduceMotion = useReducedMotion() ?? false;
  const contentRef = useRef<HTMLSpanElement>(null);
  const [visibleTokenSlots, setVisibleTokenSlots] = useState(
    MAX_VISIBLE_TOKEN_SLOTS,
  );
  const restingState = selected ? "selected" : "idle";
  const hoverState = selected ? "selectedHover" : "hover";
  const pressedState = selected ? "selectedPressed" : "pressed";

  useEffect(() => {
    const content = contentRef.current;
    if (!content) {
      return;
    }

    const syncVisibleTokenSlots = (widthPx: number) => {
      if (!Number.isFinite(widthPx) || widthPx <= 0) {
        return;
      }
      const nextSlotCount = getVisibleTokenSlotCount(widthPx);
      setVisibleTokenSlots((current) =>
        current === nextSlotCount ? current : nextSlotCount,
      );
    };

    syncVisibleTokenSlots(content.getBoundingClientRect().width);
    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        syncVisibleTokenSlots(entry.contentRect.width);
      }
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  return (
    <m.button
      animate={restingState}
      aria-label={accessibleLabel}
      aria-pressed={selected}
      className={cn("portfolio-hero-card glider-focus-ring", className)}
      data-selected={selected ? "true" : "false"}
      initial={false}
      onClick={onSelect}
      type="button"
      variants={
        reduceMotion ? REDUCED_CARD_MOTION_VARIANTS : CARD_MOTION_VARIANTS
      }
      whileHover={hoverState}
      whileTap={pressedState}
    >
      <m.img
        alt=""
        aria-hidden="true"
        className="portfolio-hero-card__image"
        decoding="async"
        src={imageSrc}
        variants={
          reduceMotion ? REDUCED_IMAGE_MOTION_VARIANTS : IMAGE_MOTION_VARIANTS
        }
      />
      <span aria-hidden="true" className="portfolio-hero-card__wash" />
      <m.span
        aria-hidden="true"
        className="portfolio-hero-card__hover-glint"
        variants={
          reduceMotion ? REDUCED_GLINT_MOTION_VARIANTS : GLINT_MOTION_VARIANTS
        }
      />
      <m.span
        animate={{ opacity: selected ? 1 : 0, scale: selected ? 1 : 0.985 }}
        aria-hidden="true"
        className="portfolio-hero-card__selection"
        initial={false}
        transition={
          reduceMotion
            ? { duration: 0 }
            : selected
              ? fluidSprings.moderate
              : fluidSprings.moderate.exit
        }
      />
      <span className="portfolio-hero-card__content" ref={contentRef}>
        {tokens.length > 0 ? (
          <StackedTokenIcons
            className="portfolio-hero-card__tokens"
            iconSize={FIGMA_TOKEN_SIZE_PX}
            maxVisible={visibleTokenSlots}
            overlap={FIGMA_TOKEN_OVERLAP_PX}
            overflowClassName="portfolio-hero-card__token-overflow ring-0 backdrop-blur-none"
            ringClassName="portfolio-hero-card__token-ring ring-0"
            showOverflowCount
            size={FIGMA_TOKEN_SIZE_PX}
            stackOrder="last-on-top"
            tokens={tokens}
          />
        ) : null}
        <span className="portfolio-hero-card__copy">
          <span className="portfolio-hero-card__title">{title}</span>
          <span className="portfolio-hero-card__performance">
            <span
              className="portfolio-hero-card__value"
              data-tone={performanceTone}
            >
              {performanceValue}
            </span>
            <span className="portfolio-hero-card__label">
              {performanceLabel}
            </span>
          </span>
        </span>
      </span>
    </m.button>
  );
}
